/*
 * FOR SALE — défilement doux de la molette et du pavé tactile, sur ordinateur (règles :
 * docs/design/transitions-pages.md § Défilement). Bibliothèque Lenis (fs-vendor-lenis.js, MIT), chargée à la demande
 * après l'événement load : rien sur téléphone, rien avant le premier affichage.
 *  - Seulement avec un pointeur fin qui survole (ordinateur). Jamais au toucher (inertie native du système), en mouvement
 *    réduit (préférence du système ou html[data-fs-motion="reduced"]), dans l'éditeur Shopify ni dans un cadre.
 *  - Inertie légère (lerp 0,12) : la page suit la molette sans traîner derrière elle.
 *  - Jamais pendant un dialogue modal : la page reste immobile, le dialogue défile seul. Toute zone qui défile elle-même
 *    (liste, tableau, rail, menu) garde la molette ; data-lenis-prevent exclut une zone explicitement.
 *  - Clavier, barre de défilement, ancres et scrollIntoView restent natifs (Lenis suit la position réelle).
 *  - Boucle d'images à la demande : requestAnimationFrame seulement pendant un défilement doux, de la première impulsion
 *    à l'arrêt de l'inertie. Page au repos : aucune image demandée (la boucle permanente de Lenis, autoRaf, coûtait
 *    ~15 ms de fil principal par seconde, mesuré sur l'écran d'un jeu).
 *  - Intention du défilement (window.FSScroll.intent) : « read » quand le visiteur fait défiler lui-même (molette, pavé,
 *    toucher, touches de défilement, barre de défilement), « navigate » quand un clic, une touche d'action, une ancre ou
 *    l'historique l'emmène quelque part (le défilement qui suit appartient à cette navigation). Tout contenu qui grandit
 *    au défilement (défilement continu du catalogue, fs-filters.js) ne réagit qu'à « read » : un trajet vers une cible
 *    plus bas (#sell, pied de page, retour) ne doit rien insérer au-dessus d'elle, sinon le trajet s'arrête court.
 * window.FSScroll : { active, intent, stop(), start() }.
 */
export const LERP = 0.12;
export const FRAME_MS = 1000 / 60;

/**
 * Boucle d'images à la demande pour une instance Lenis créée avec autoRaf: false. kick() démarre la boucle (impulsion de
 * molette) ; elle s'arrête d'elle-même quand l'inertie est finie (isScrolling n'est plus 'smooth'). Première image d'une
 * reprise : un pas d'une image (sinon Lenis compterait tout le temps de repos et sauterait au but).
 */
export function frameLoop(lenis, raf, cancel) {
  let id = 0;
  let fresh = true;
  const tick = (time) => {
    id = 0;
    if (fresh) { lenis.time = time - FRAME_MS; fresh = false; }
    lenis.raf(time);
    if (lenis.isScrolling === 'smooth') id = raf(tick);
    else fresh = true;
  };
  return {
    kick() { if (!id) id = raf(tick); },
    cancel() { if (id) cancel(id); id = 0; fresh = true; },
    get running() { return Boolean(id); }
  };
}

/** Touches qui font défiler la page quand le focus n'est pas dans un champ ou une commande. */
const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ', 'Spacebar']);
const CONTROL = 'input, textarea, select, button, summary, [contenteditable=""], [contenteditable="true"], [role="slider"], [role="spinbutton"], [role="listbox"], [role="menu"], [role="tablist"], [role="radiogroup"]';

/**
 * Intention donnée par un évènement : 'read' (le visiteur fait défiler la page lui-même), 'navigate' (il active quelque
 * chose : le défilement qui suit est un trajet), '' (sans effet). `root` : documentElement (barre de défilement de la page).
 */
export function scrollIntent(event, root) {
  switch (event?.type) {
    case 'wheel': return event.ctrlKey ? '' : 'read';
    case 'touchmove': return 'read';
    case 'pointerdown': {
      if (event.button === 1) return 'read';
      const onBar = event.target === root && root && (event.clientX >= root.clientWidth || event.clientY >= root.clientHeight);
      return onBar ? 'read' : '';
    }
    case 'keydown': {
      if (!SCROLL_KEYS.has(event.key)) return event.key === 'Tab' || event.key === 'Enter' ? 'navigate' : '';
      return event.target?.closest?.(CONTROL) ? '' : 'read';
    }
    case 'click': case 'hashchange': case 'popstate': return 'navigate';
    default: return '';
  }
}

export function smoothScrollAllowed({ finePointer, reducedMotion, editor, framed } = {}) {
  return Boolean(finePointer) && !reducedMotion && !editor && !framed;
}

function boot(win, doc) {
  const html = doc.documentElement;
  const fine = win.matchMedia('(hover: hover) and (pointer: fine)');
  const reduce = win.matchMedia('(prefers-reduced-motion: reduce)');
  let lenis = null;
  let loop = null;
  let loading = false;
  let framed = true;
  try { framed = win.self !== win.top; } catch { framed = true; }

  const allowed = () => smoothScrollAllowed({
    finePointer: fine.matches,
    reducedMotion: reduce.matches || html.getAttribute('data-fs-motion') === 'reduced',
    editor: html.classList.contains('shopify-design-mode'),
    framed
  });
  const modalOpen = () => { try { return Boolean(doc.querySelector('dialog:modal')); } catch { return false; } };

  const start = () => {
    if (lenis || loading) return;
    loading = true;
    import('./fs-vendor-lenis.js').then(({ default: Lenis }) => {
      loading = false;
      if (lenis || !allowed()) return;
      lenis = new Lenis({
        lerp: LERP,
        smoothWheel: true,
        syncTouch: false,
        autoRaf: false,
        allowNestedScroll: true,
        stopInertiaOnNavigate: true,
        prevent: (node) => node.tagName === 'DIALOG',
        virtualScroll: () => !modalOpen()
      });
      loop = frameLoop(lenis, (fn) => win.requestAnimationFrame(fn), (id) => win.cancelAnimationFrame(id));
      lenis.on('virtual-scroll', loop.kick);
      if (modalOpen()) lenis.stop();
    }).catch(() => { loading = false; });
  };
  const stop = () => {
    if (!lenis) return;
    loop?.cancel();
    loop = null;
    lenis.destroy();
    lenis = null;
  };
  const setup = () => { if (allowed()) start(); else stop(); };

  /* Dialogue modal : la page s'arrête net sous le voile, puis reprend à la fermeture du dernier. */
  doc.addEventListener('toggle', (event) => {
    if (!lenis || !event.target || event.target.tagName !== 'DIALOG') return;
    if (modalOpen()) lenis.stop(); else lenis.start();
  }, true);
  [fine, reduce].forEach((query) => query.addEventListener?.('change', setup));
  if (typeof win.MutationObserver === 'function') new win.MutationObserver(setup).observe(html, { attributes: true, attributeFilter: ['data-fs-motion'] });

  /* Intention du défilement, posée avant tout autre écouteur (phase de capture sur window). Arrivée : « navigate » (la
     position vient du navigateur, d'une ancre ou du retour), jusqu'au premier geste de défilement du visiteur. */
  let intent = 'navigate';
  const track = (event) => { const next = scrollIntent(event, html); if (next) intent = next; };
  ['wheel', 'touchmove', 'pointerdown', 'keydown', 'click', 'hashchange', 'popstate'].forEach((type) => win.addEventListener(type, track, { capture: true, passive: true }));

  win.FSScroll = Object.freeze({
    get active() { return Boolean(lenis); },
    get intent() { return intent; },
    stop() { lenis?.stop(); },
    start() { lenis?.start(); }
  });
  if (doc.readyState === 'complete') setup(); else win.addEventListener('load', setup, { once: true });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.FSScroll) boot(window, document);
